import dayjs from 'dayjs'

import _dayjs from 'src/Helpers/dayjs.js'
import { MetaAdsCampaignDetails } from 'src/Redux/metaAdsDetailsApi'
import { numberFormatCommas } from 'src/Constants/functions'

export const getDurationDays = (
  startDate?: string,
  endDate?: string,
): number => {
  const start = _dayjs(startDate).utc().startOf('day')
  const end = _dayjs(endDate).utc().startOf('day')
  return Math.max(0, end.diff(start, 'day'))
}

export const getCampaignBudget = (
  campaign: Pick<
    MetaAdsCampaignDetails,
    'startDate' | 'endDate' | 'dailyBudget' | 'totalBudget'
  >,
): number | null | undefined => {
  const { startDate, endDate, dailyBudget, totalBudget } = campaign
  if (!startDate || !endDate || typeof dailyBudget !== 'number') {
    return totalBudget
  }
  const days = getDurationDays(startDate, endDate)
  return Math.round(dailyBudget * days * 100) / 100
}

export const getSpentAmount = (
  campaign: Pick<
    MetaAdsCampaignDetails,
    'startDate' | 'endDate' | 'status' | 'dailyBudget' | 'totalBudget'
  >,
): number => {
  const start = dayjs(campaign.startDate).utc().startOf('day')
  const today = dayjs().utc().startOf('day')

  const endBoundary = campaign.endDate
    ? dayjs(campaign.endDate).utc().startOf('day')
    : today

  const effectiveEnd = endBoundary.isBefore(today) ? endBoundary : today

  let daysPassed = effectiveEnd.diff(start, 'day') + 1
  if (start.isAfter(effectiveEnd)) {
    daysPassed = 0
  }
  if (daysPassed < 0) {
    daysPassed = 0
  }

  const daily = campaign.dailyBudget ?? 0
  const rawSpent = Math.max(0, daysPassed * daily)

  const budget = getCampaignBudget(campaign)
  const totalCap = budget === null ? rawSpent : Math.min(rawSpent, budget ?? 0)

  return Math.max(0, totalCap)
}

export const getCostPerFollower = (
  spent: number,
  followersDelta: number,
): number => {
  const safeDelta = Math.max(followersDelta, 0)
  return safeDelta === 0 ? 0 : spent / safeDelta
}

export const getCTR = (clicks?: number, impressions?: number): string => {
  if (!clicks || !impressions) {
    return '0%'
  }
  const ratio = (clicks / impressions) * 100
  return `${numberFormatCommas(ratio)}%`
}

export const getMetaAdsAutoRenewVisibility = (
  status?: string,
  shouldAutoRenew?: boolean,
): {
  isAutoRenewActiveVisible: boolean
  isAutoRenewOffVisible: boolean
} => {
  const isVisible = status === 'scheduled' || status === 'running'
  return {
    isAutoRenewActiveVisible: isVisible && Boolean(shouldAutoRenew),
    isAutoRenewOffVisible: isVisible && !shouldAutoRenew,
  }
}

export const getMetaAdsVisibilityFlags = (
  status?: string,
  chartData?: unknown,
): {
  isInfoBlockVisible: boolean
  isPerformanceOverviewVisible: boolean
  isChartVisible: boolean
} => ({
  isInfoBlockVisible:
    status === 'scheduled' || status === 'approved' || status === 'ended',
  isPerformanceOverviewVisible: status === 'running' || status === 'ended',
  isChartVisible:
    Boolean(chartData) && (status === 'running' || status === 'ended'),
})

const isPerformanceVisible = (status?: string, data?: unknown[]): boolean =>
  ['running', 'ended'].includes(status ?? '') && (!data || data.length > 0)

export const getMetaAdsVisualPerformance = (
  status?: string,
  data?: unknown[],
): {
  isAdVisualPerformanceVisible: boolean
} => ({
  isAdVisualPerformanceVisible: isPerformanceVisible(status, data),
})

export const getMetaAdsCountiesPerformance = (
  status?: string,
  data?: unknown[],
): {
  isCountryPerformanceVisible: boolean
} => ({
  isCountryPerformanceVisible: isPerformanceVisible(status, data),
})

export const getDurationAndDates = (
  startDate?: string,
  endDate?: string,
): { duration: string; dates: string } => {
  if (!startDate || !endDate) {
    return { duration: '', dates: '' }
  }
  const start = _dayjs(startDate).utc().startOf('day')
  const end = _dayjs(endDate).utc().startOf('day')
  const days = getDurationDays(startDate, endDate)
  const duration = `${days} ${days === 1 ? 'day' : 'days'}`
  const dates = `(${start.format('DD MMM, YYYY')} - ${end.format('DD MMM, YYYY')})`
  return { duration, dates }
}
